import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as Scrivito from "scrivito";
import { isEqual } from "lodash-es";
import { ProductListQuery } from "api/queries";
import { fetchData } from "hooks";
import { getHomepage } from "utils";

const initialState = {
  productListData: [],
  allProducts: [],
  isLoading: false,
  waitingFor3dModel: false,
  error: null,
  productCategoryId: [],
  productsContent: [],
  categoryHeadline: "",
  isImageFetching: false,
  productListPage: null,
  productConfiguratorPage: null,
  createProductAction: null,
  cacheKey: "",
  sidebarChangeRef: { current: false },
};

export const useProductsOverviewStore = create(
  persist(
    (set, get) => ({
      ...initialState,
      actions: {
        getProductsData: async ({ lang, currentUserTenant }) => {
          if (typeof currentUserTenant !== "string") {
            console.error(
              `TypeError, currentUserTenant of type '${typeof currentUserTenant}' is not a string`,
            );
            return;
          }
          if (typeof lang !== "string") {
            console.error(
              `TypeError, lang of type '${typeof lang}' is not a string`,
            );
            return;
          }
          const newCacheKey = lang + currentUserTenant;
          if (get().cacheKey === newCacheKey) {
            return;
          }
          set({ isLoading: true, error: null });

          Scrivito.load(() => {
            const homepage = getHomepage();
            const productListPage = homepage.get("productListPage");
            const productConfiguratorPage = homepage.get(
              "productConfiguratorPage",
            );
            set({
              productListPage,
              productConfiguratorPage,
            });
          });

          fetchData({
            query: ProductListQuery,
            variables: {
              input: {
                client: "web",
                languageCode: lang,
                countryCode: currentUserTenant,
              },
            },
            requestPolicy: "cache-first",
            onSuccess: ({ data }) => {
              const result = data.seriesNodes;
              get().actions.addKeyLists([], result);
              set({
                productListData: result,
                isLoading: false,
                cacheKey: newCacheKey,
              });
              get().actions.generateAllProducts({
                lang,
                productListData: result,
              });
            },
            onError: (error) => set({ error, isLoading: false }),
          });
        },

        generateAllProducts: async ({ lang, productListData }) => {
          await Scrivito.load(() =>
            Scrivito.Obj.where("_objClass", "equals", "WizardItem")
              .and("categoryLanguages", "equals", lang)
              .take()
              .filter((i) => i.get("linkForMenuItem"))
              .map((link) => ({
                name: link.get("title"),
                image: link.get("imageForProductsNavigation") || null,
                key: link.id(),
                link,
              })),
          )
            .then((extraLinks) => {
              const productsListWithWizards = [
                ...productListData,
                ...extraLinks,
              ];
              set({ allProducts: productsListWithWizards });
            })
            .catch((err) =>
              set({ error: err.message || "Error preparing products" }),
            )
            .finally(() => set({ isLoading: false }));
        },

        addKeyLists: (parentKeyList, prods = []) =>
          prods.map((item) => {
            const list = [...parentKeyList, item.key];
            item.keyList = list;
            if (item.nodes?.length) {
              get().actions.addKeyLists(list, item.nodes);
            }
            return item;
          }),

        addImagesToProduct: async (nodes = []) => {
          const pimIds = nodes.map((i) => i.pimId);
          const imageList = await Scrivito.load(() =>
            Scrivito.Obj.where("_objClass", "equals", "Image")
              .and("pimId", "equals", pimIds)
              .take(),
          );

          return nodes.map((node) => {
            const image = imageList.find((i) => i.get("pimId") === node.pimId);
            return { ...node, image };
          });
        },

        setProductCategoryId: (newVal) =>
          set((state) => {
            if (!isEqual(state.productCategoryId, newVal)) {
              return { productCategoryId: newVal };
            }
            return state;
          }),

        markSidebarChange: () =>
          set((state) => {
            state.sidebarChangeRef.current = true;
            return state;
          }),
        resetSidebarChange: () =>
          set((state) => {
            state.sidebarChangeRef.current = false;
            return state;
          }),
        imagesHandler: async (activeCategory = { nodes: [] }) => {
          const { nodes } = activeCategory;
          if (!nodes) {
            return;
          }

          set({ isImageFetching: true });
          const imagesOfNode = await get().actions.addImagesToProduct(nodes);
          set({ productsContent: imagesOfNode, isImageFetching: false });
        },

        setProductsContent: (content) => {
          set({ productsContent: content });
        },

        setCategoryHeadline: (headline) => {
          set({ categoryHeadline: headline });
        },

        setWaitingFor3dModel: (value) => {
          set({ waitingFor3dModel: value });
        },

        resetProductConfiguratorStore: () => set(initialState),

        navigateToProductListPage: (params = {}) => {
          const { productListPage } = get();
          if (productListPage) {
            Scrivito.navigateTo(() => productListPage, { params });
          }
        },

        navigateToConfiguratorPage: (params = {}) => {
          const { productConfiguratorPage } = get();
          if (productConfiguratorPage) {
            Scrivito.navigateTo(() => productConfiguratorPage, { params });
          }
        },
      },
    }),
    {
      name: "product-configurator",
      partialize: (state) => ({
        productCategoryId: state.productCategoryId,
      }),
    },
  ),
);

export const useProductsOverviewActions = () =>
  useProductsOverviewStore((state) => state.actions);

// Todo: implement the below in a refactoring topic to
// - reduce overall code bloat of product list logic
// - improve perforamnce of product list page
// - reduce complexity of product list logic
